(() => {
  if (window.customElements.get('qk-creator-videos')) {
    return;
  }

  class QkCreatorVideos extends HTMLElement {
    connectedCallback() {
      window.customElements.whenDefined('scroll-carousel').then(() => {
        if (this.isConnected) {
          this.initialize();
        }
      });
    }

    disconnectedCallback() {
      this.cleanupExpandedVideo({ restoreFocus: false });

      if (this.dialog?.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
      }

      this.abortController?.abort();
      this.viewportObserver?.disconnect();
      this.pauseAll();
      this.initialized = false;
    }

    initialize() {
      if (this.initialized) {
        return;
      }

      this.carousel = this.querySelector('scroll-carousel');
      this.cards = Array.from(this.querySelectorAll('[data-qk-creator-video-card]'));
      this.dialog = this.querySelector('[data-qk-creator-video-dialog]');
      this.dialogStage = this.dialog?.querySelector('[data-qk-creator-video-dialog-stage]');
      this.dialogTitle = this.dialog?.querySelector('[data-qk-creator-video-dialog-title]');
      this.dialogPlayButton = this.dialog?.querySelector('[data-qk-creator-video-dialog-play]');
      this.dialogSoundButton = this.dialog?.querySelector('[data-qk-creator-video-dialog-sound]');
      this.dialogScrubber = this.dialog?.querySelector('[data-qk-creator-video-dialog-scrubber]');

      if (!this.carousel || this.cards.length === 0) {
        return;
      }

      this.initialized = true;
      this.selectedIndex = 0;
      this.isNearViewport = false;
      this.userPausedCard = null;
      this.completedCard = null;
      this.abortController = new AbortController();
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const { signal } = this.abortController;

      this.addEventListener('carousel:select', (event) => {
        if (event.target !== this.carousel) {
          return;
        }

        this.selectCard(Number(event.detail?.index) || 0);
      }, { signal });

      this.cards.forEach((card, index) => {
        card.querySelector('[data-qk-creator-video-play]')?.addEventListener('click', () => {
          const video = card.querySelector('video');
          const wasPlaying = Boolean(video && !video.paused);

          this.carousel.select(index);

          if (wasPlaying) {
            this.userPausedCard = card;
            this.pauseCard(card);
            return;
          }

          this.userPausedCard = null;
          this.completedCard = null;
          this.selectCard(index, { play: true, userInitiated: true });
        }, { signal });

        card.querySelector('[data-qk-creator-video-sound]')?.addEventListener('click', () => {
          this.userPausedCard = null;
          this.completedCard = null;
          this.carousel.select(index);
          this.selectCard(index, { play: true, userInitiated: true });
          this.toggleSound(card);
        }, { signal });

        card.querySelector('[data-qk-creator-video-expand]')?.addEventListener('click', (event) => {
          this.carousel.select(index);
          this.openExpandedVideo(card, event.currentTarget);
        }, { signal });
      });

      this.dialog?.querySelector('[data-qk-creator-video-dialog-close]')?.addEventListener('click', () => {
        this.closeExpandedVideo();
      }, { signal });

      this.dialogPlayButton?.addEventListener('click', () => {
        this.toggleDialogPlayback();
      }, { signal });

      this.dialogSoundButton?.addEventListener('click', () => {
        this.toggleDialogSound();
      }, { signal });

      this.dialogScrubber?.addEventListener('input', (event) => {
        this.scrubDialogVideo(event.currentTarget);
      }, { signal });

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          this.closeExpandedVideo();
        }
      }, { signal });

      this.dialog?.addEventListener('close', () => {
        this.cleanupExpandedVideo();
      }, { signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.dialogVideo?.pause();
          this.pauseAll();
        } else if (this.isNearViewport && !this.motionQuery.matches) {
          this.resumeSelectedCard();
        }
      }, { signal });

      this.motionQuery.addEventListener('change', () => {
        if (this.motionQuery.matches) {
          this.pauseAll();
        } else if (this.isNearViewport) {
          this.resumeSelectedCard();
        }
      }, { signal });

      this.viewportObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.isNearViewport = Boolean(entry?.isIntersecting);

        if (!this.isNearViewport) {
          this.pauseAll();
          return;
        }

        if (!this.motionQuery.matches) {
          this.resumeSelectedCard();
        }
      }, {
        rootMargin: '160px 0px',
        threshold: 0.01,
      });

      this.viewportObserver.observe(this);
      this.selectCard(0, { play: false });
    }

    selectCard(index, { play = true, userInitiated = false } = {}) {
      const safeIndex = Math.max(0, Math.min(index, this.cards.length - 1));
      const previousCard = this.cards[this.selectedIndex];
      const selectedCard = this.cards[safeIndex];

      if (selectedCard !== previousCard) {
        this.userPausedCard = null;
        this.completedCard = null;
      }

      this.selectedIndex = safeIndex;

      this.cards.forEach((card, cardIndex) => {
        card.classList.toggle('is-selected', cardIndex === safeIndex);

        if (cardIndex !== safeIndex) {
          this.pauseCard(card);
        }
      });

      if (
        !play
        || (!userInitiated && this.userPausedCard === selectedCard)
        || (!userInitiated && this.completedCard === selectedCard)
        || (!userInitiated && (!this.isNearViewport || this.motionQuery.matches))
      ) {
        return;
      }

      this.playCard(safeIndex);
    }

    resumeSelectedCard() {
      const selectedCard = this.cards[this.selectedIndex];

      if (selectedCard !== this.userPausedCard && selectedCard !== this.completedCard) {
        this.playCard(this.selectedIndex);
      }
    }

    mountVideo(card) {
      const mountedVideo = card.querySelector('video');

      if (mountedVideo) {
        return mountedVideo;
      }

      const template = card.querySelector('[data-qk-creator-video-template]');
      const media = card.querySelector('.qk-creator-videos__media');

      if (!template || !media) {
        return null;
      }

      const fragment = template.content.cloneNode(true);
      const video = fragment.querySelector('video');

      if (!video) {
        return null;
      }

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      media.appendChild(fragment);
      card.classList.add('is-mounted');
      this.syncSoundButton(card, video);
      this.syncPlayButton(card, video);

      video.addEventListener('loadstart', () => card.classList.add('is-loading'));
      video.addEventListener('waiting', () => card.classList.add('is-loading'));
      video.addEventListener('canplay', () => card.classList.remove('is-loading'));
      video.addEventListener('playing', () => {
        card.classList.remove('is-loading');
        card.classList.add('is-playing');
        this.syncPlayButton(card, video);
        this.syncDialogPlayback(video);
      });
      video.addEventListener('pause', () => {
        card.classList.remove('is-playing');
        this.syncPlayButton(card, video);
        this.syncDialogPlayback(video);
      });
      video.addEventListener('ended', () => {
        this.completedCard = card;
        card.classList.remove('is-loading', 'is-playing');
        this.syncPlayButton(card, video);
        this.syncDialogPlayback(video);
        this.syncDialogTimeline(video);
      });
      video.addEventListener('timeupdate', () => this.syncDialogTimeline(video));
      video.addEventListener('durationchange', () => this.syncDialogTimeline(video));
      video.addEventListener('loadedmetadata', () => this.syncDialogTimeline(video));
      video.addEventListener('volumechange', () => {
        this.syncSoundButton(card, video);
        this.syncDialogSound(video);
      });
      video.addEventListener('error', () => {
        card.classList.remove('is-loading', 'is-playing');
        card.classList.add('has-error');
        this.syncPlayButton(card, video);
      });

      return video;
    }

    playCard(index) {
      const card = this.cards[index];

      if (!card || document.hidden) {
        return;
      }

      this.cards.forEach((otherCard, otherIndex) => {
        if (otherIndex !== index) {
          this.pauseCard(otherCard);
        }
      });

      const video = this.mountVideo(card);

      if (!video) {
        return;
      }

      card.classList.remove('has-error');
      card.classList.add('is-loading');

      if (video.ended) {
        video.currentTime = 0;
      }

      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          card.classList.remove('is-loading', 'is-playing');
        });
      }
    }

    pauseCard(card) {
      const video = card?.querySelector('video');

      if (video && !video.paused) {
        video.pause();
      }

      card?.classList.remove('is-loading', 'is-playing');

      if (video) {
        this.syncPlayButton(card, video);
      }
    }

    pauseAll() {
      this.cards?.forEach((card) => this.pauseCard(card));
    }

    openExpandedVideo(card, opener) {
      if (!this.dialog || !this.dialogStage || this.dialog.open) {
        return;
      }

      const video = this.mountVideo(card);
      const media = card.querySelector('.qk-creator-videos__media');

      if (!video || !media) {
        return;
      }

      this.userPausedCard = card;
      this.completedCard = null;
      this.pauseCard(card);
      this.dialogCard = card;
      this.dialogMedia = media;
      this.dialogOpener = opener;
      this.dialogVideo = video;
      opener?.setAttribute('aria-expanded', 'true');

      const creatorName = card.querySelector('.qk-creator-videos__creator')?.textContent?.trim();
      const label = card.dataset.qkCreatorVideoLabel || 'Videoclip creator';

      if (this.dialogTitle) {
        this.dialogTitle.textContent = creatorName ? `Videoclip cu ${creatorName}` : label;
      }

      video.controls = false;
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
      video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback nofullscreen');
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('disableremoteplayback', '');
      video.classList.add('qk-creator-videos__dialog-video');
      video.removeAttribute('aria-hidden');
      video.setAttribute('aria-label', label);
      video.setAttribute('tabindex', '-1');
      this.dialogStage.appendChild(video);
      this.syncDialogControls(video);
      document.documentElement.classList.add('qk-creator-video-dialog-open');

      try {
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }
      } catch (_error) {
        this.cleanupExpandedVideo();
        return;
      }

      if (video.ended) {
        video.currentTime = 0;
      }

      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    closeExpandedVideo() {
      if (!this.dialog) {
        return;
      }

      if (typeof this.dialog.close === 'function' && (this.dialog.open || this.dialog.hasAttribute('open'))) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.cleanupExpandedVideo();
      }
    }

    cleanupExpandedVideo({ restoreFocus = true } = {}) {
      const video = this.dialogVideo;
      const card = this.dialogCard;
      const opener = this.dialogOpener;

      if (video) {
        video.pause();
        video.controls = false;
        video.classList.remove('qk-creator-videos__dialog-video');
        video.setAttribute('aria-hidden', 'true');
        video.setAttribute('tabindex', '-1');
        video.removeAttribute('aria-label');
        this.dialogMedia?.appendChild(video);

        if (card) {
          this.userPausedCard = card;
          this.syncPlayButton(card, video);
          this.syncSoundButton(card, video);
        }
      }

      document.documentElement.classList.remove('qk-creator-video-dialog-open');
      this.dialogVideo = null;
      this.dialogCard = null;
      this.dialogMedia = null;
      this.dialogOpener = null;

      opener?.setAttribute('aria-expanded', 'false');

      if (restoreFocus && opener?.isConnected) {
        opener.focus();
      }
    }

    toggleDialogPlayback() {
      const video = this.dialogVideo;

      if (!video) {
        return;
      }

      if (!video.paused && !video.ended) {
        video.pause();
        return;
      }

      if (video.ended) {
        video.currentTime = 0;
      }

      this.completedCard = null;
      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    toggleDialogSound() {
      const video = this.dialogVideo;

      if (!video) {
        return;
      }

      video.muted = !video.muted;
      this.syncDialogSound(video);

      if (this.dialogCard) {
        this.syncSoundButton(this.dialogCard, video);
      }
    }

    scrubDialogVideo(scrubber) {
      const video = this.dialogVideo;
      const duration = video?.duration;

      if (!video || scrubber !== this.dialogScrubber || !Number.isFinite(duration) || duration <= 0) {
        return;
      }

      const maximum = Number(scrubber.max) || 1000;
      const progress = Math.max(0, Math.min(1, Number(scrubber.value) / maximum));
      video.currentTime = progress * duration;

      if (progress < 1) {
        this.completedCard = null;
      }

      this.syncDialogTimeline(video);
    }

    syncDialogControls(video) {
      this.syncDialogPlayback(video);
      this.syncDialogSound(video);
      this.syncDialogTimeline(video);
    }

    syncDialogPlayback(video) {
      if (!this.dialogPlayButton || video !== this.dialogVideo) {
        return;
      }

      const isPlaying = !video.paused && !video.ended;
      this.dialogPlayButton.setAttribute('aria-pressed', String(isPlaying));
      this.dialogPlayButton.setAttribute('aria-label', isPlaying ? 'Pune videoclipul pe pauză' : 'Redă videoclipul');
    }

    syncDialogSound(video) {
      if (!this.dialogSoundButton || video !== this.dialogVideo) {
        return;
      }

      const isAudible = !video.muted;
      this.dialogSoundButton.setAttribute('aria-pressed', String(isAudible));
      this.dialogSoundButton.setAttribute('aria-label', isAudible ? 'Oprește sunetul' : 'Pornește sunetul');
    }

    syncDialogTimeline(video) {
      if (!this.dialogScrubber || video !== this.dialogVideo) {
        return;
      }

      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
      const currentTime = duration > 0 ? Math.max(0, Math.min(video.currentTime, duration)) : 0;
      const progress = duration > 0 ? currentTime / duration : 0;
      const maximum = Number(this.dialogScrubber.max) || 1000;

      this.dialogScrubber.disabled = duration <= 0;
      this.dialogScrubber.value = String(Math.round(progress * maximum));
      this.dialogScrubber.style.setProperty('--qk-creator-video-progress', `${progress * 100}%`);
      this.dialogScrubber.setAttribute(
        'aria-valuetext',
        `${this.formatDialogTime(currentTime)} din ${this.formatDialogTime(duration)}`,
      );
    }

    formatDialogTime(seconds) {
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
      const minutes = Math.floor(safeSeconds / 60);
      const remainingSeconds = String(safeSeconds % 60).padStart(2, '0');
      return `${minutes}:${remainingSeconds}`;
    }

    toggleSound(card) {
      const video = this.mountVideo(card);

      if (!video) {
        return;
      }

      video.muted = !video.muted;
      this.syncSoundButton(card, video);

      if (video.paused) {
        this.playCard(this.cards.indexOf(card));
      }
    }

    syncSoundButton(card, video) {
      const button = card.querySelector('[data-qk-creator-video-sound]');

      if (!button) {
        return;
      }

      const creatorName = card.querySelector('.qk-creator-videos__creator')?.textContent?.trim() || 'creator';
      const isAudible = !video.muted;
      button.setAttribute('aria-pressed', String(isAudible));
      button.setAttribute('aria-label', `${isAudible ? 'Oprește' : 'Pornește'} sunetul pentru ${creatorName}`);
    }

    syncPlayButton(card, video) {
      const button = card?.querySelector('[data-qk-creator-video-play]');

      if (!button || !video) {
        return;
      }

      const label = card.dataset.qkCreatorVideoLabel || 'videoclip';
      const isPlaying = !video.paused && !video.ended;
      button.setAttribute('aria-pressed', String(isPlaying));
      button.setAttribute('aria-label', `${isPlaying ? 'Pauză' : 'Redă'}: ${label}`);
    }
  }

  window.customElements.define('qk-creator-videos', QkCreatorVideos);
})();
